import { regulationsData } from "./regulationsData";
import { assetUrl } from "@/utils/asset";

// ------------------------------------------------------------------------------------
// Types
// ------------------------------------------------------------------------------------

import { API_BASE_URL } from "@/src/services/api";

// ------------------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------------------

const slugify = value => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const toArray = value => Array.isArray(value) ? value : [];
const normalizeEventsFromRaw = org => toArray(org.events).map(event => ({
  ...event,
  id: String(event.id),
  image: assetUrl(event.image),
  organizer: {
    name: event.organizer,
    contact: "",
    website: org.website ?? ""
  }
}));
const normalizeBackendEvents = (events, defaultOrganizerName, defaultWebsite) => {
  if (!Array.isArray(events)) return [];
  return events.map((event, index) => {
    const source = event;
    const organizerSource = source.organizer;
    let organizer;
    if (typeof organizerSource === "string") {
      organizer = {
        name: organizerSource,
        contact: "",
        website: defaultWebsite ?? ""
      };
    } else if (organizerSource && typeof organizerSource === "object") {
      organizer = {
        name: organizerSource.name ?? defaultOrganizerName ?? "",
        contact: organizerSource.contact ?? "",
        website: organizerSource.website ?? defaultWebsite ?? ""
      };
    } else {
      organizer = {
        name: defaultOrganizerName ?? "",
        contact: "",
        website: defaultWebsite ?? ""
      };
    }
    return {
      ...source,
      id: source.id ? String(source.id) : String(index + 1),
      image: source.image ? assetUrl(source.image) : assetUrl("/PlaceholderImgs/meeting.webp"),
      organizer
    };
  });
};
const convertRawOrganizationToDetail = raw => {
  const contact = raw.infoSection?.contact ?? {};
  const location = raw.infoSection?.location ?? {};
  const social = raw.infoSection?.social ?? {};
  return {
    id: raw.id,
    slug: raw.slug,
    orgName: raw.orgName,
    orgDescripTitle: raw.orgDescripTitle ?? "About Us",
    orgDescrip: raw.orgDescrip,
    logoUrl: assetUrl(raw.logoUrl),
    bannerImages: toArray(raw.bannerImages).map(img => assetUrl(img)),
    infoSection: {
      contact: {
        title: contact.title ?? "Contact Us",
        company: contact.company ?? raw.orgName,
        email: contact.email ?? "",
        phone: contact.phone ?? ""
      },
      location: {
        title: location.title ?? "Location",
        company: location.company ?? raw.orgName,
        address: location.address ?? "",
        city: location.city ?? "",
        address2: location.address2 ?? "",
        state: location.state ?? "",
        postalCode: location.postalCode ?? "",
        country: location.country ?? ""
      },
      social: {
        title: social.title ?? "Follow Us",
        facebook: social.facebook ?? "",
        instagram: social.instagram ?? "",
        twitter: social.twitter ?? "",
        linkedIn: social.linkedIn ?? "",
        youtube: social.youtube ?? "",
        whatsApp: social.whatsApp ?? "",
        tikTok: social.tikTok ?? "",
        telegram: social.telegram ?? ""
      }
    },
    orgType: toArray(raw.orgType),
    orgIndustryFocus: toArray(raw.orgIndustryFocus),
    orgFocus: toArray(raw.orgFocus),
    productFocus: toArray(raw.productFocus),
    regionalFocus: toArray(raw.regionalFocus),
    establishedYear: raw.establishedYear ?? new Date().getFullYear(),
    employees: raw.employees ?? "",
    website: raw.website ?? "",
    description: raw.description ?? "",
    services: toArray(raw.services).map(service => ({
      ...service,
      image: assetUrl(service.image)
    })),
    events: normalizeEventsFromRaw(raw),
    regulations: toArray(raw.regulations)
  };
};
const convertDetailToListItem = org => {
  const listItem = {
    id: org.id,
    slug: org.slug,
    orgName: org.orgName,
    description: org.description,
    logoUrl: assetUrl(org.logoUrl),
    bannerImages: org.bannerImages.map(img => assetUrl(img)),
    orgType: [...org.orgType],
    orgIndustryFocus: [...org.orgIndustryFocus],
    orgFocus: [...org.orgFocus],
    productFocus: [...org.productFocus],
    regionalFocus: [...org.regionalFocus],
    establishedYear: org.establishedYear,
    employees: org.employees,
    website: org.website,
    services: org.services,
    events: org.events,
    regulations: org.regulations,
    infoSection: {
      location: {
        city: org.infoSection.location.city,
        address: org.infoSection.location.address || null,
        address2: org.infoSection.location.address2 || null,
        state: org.infoSection.location.state || null,
        postalCode: org.infoSection.location.postalCode || null,
        country: org.infoSection.location.country || null
      }
    }
  };
  if (org.backendId) {
    listItem.backendId = org.backendId;
  }
  return listItem;
};
const findStaticOrganization = identifier => {
  if (!identifier) return undefined;
  const normalized = identifier.toLowerCase();
  return Object.values(organizationsData).find(org => {
    const slug = String(org.slug || "").toLowerCase();
    const id = String(org.id || "").toLowerCase();
    return slug === normalized || id === normalized;
  });
};
const createPlaceholderOrganization = payload => {
  const fallbackName = payload.name ?? payload.orgName ?? "Untitled Organization";
  const derivedSlug = payload.slug ?? (fallbackName ? slugify(fallbackName) : undefined) ?? (payload.id ? slugify(payload.id) : "organization");
  return {
    id: payload.id ?? derivedSlug,
    backendId: payload.id,
    slug: derivedSlug,
    orgName: fallbackName,
    orgDescripTitle: payload.orgDescripTitle ?? "About Us",
    orgDescrip: payload.orgDescrip ?? payload.description ?? "",
    logoUrl: payload.logoUrl ? assetUrl(payload.logoUrl) : "",
    bannerImages: toArray(payload.bannerImages).map(img => assetUrl(img)),
    infoSection: {
      contact: {
        title: payload.infoSection?.contact?.title ?? "Contact",
        company: payload.infoSection?.contact?.company ?? fallbackName,
        email: payload.infoSection?.contact?.email ?? "",
        phone: payload.infoSection?.contact?.phone ?? ""
      },
      location: {
        title: payload.infoSection?.location?.title ?? "Location",
        company: payload.infoSection?.location?.company ?? fallbackName,
        address: payload.infoSection?.location?.address ?? "",
        city: payload.infoSection?.location?.city ?? "",
        address2: payload.infoSection?.location?.address2 ?? "",
        state: payload.infoSection?.location?.state ?? "",
        postalCode: payload.infoSection?.location?.postalCode ?? "",
        country: payload.infoSection?.location?.country ?? ""
      },
      social: {
        title: payload.infoSection?.social?.title ?? "Follow Us",
        facebook: payload.infoSection?.social?.facebook ?? "",
        instagram: payload.infoSection?.social?.instagram ?? "",
        twitter: payload.infoSection?.social?.twitter ?? "",
        linkedIn: payload.infoSection?.social?.linkedIn ?? "",
        youtube: payload.infoSection?.social?.youtube ?? "",
        whatsApp: payload.infoSection?.social?.whatsApp ?? "",
        tikTok: payload.infoSection?.social?.tikTok ?? "",
        telegram: payload.infoSection?.social?.telegram ?? ""
      }
    },
    orgType: toArray(payload.orgType),
    orgIndustryFocus: toArray(payload.orgIndustryFocus),
    orgFocus: toArray(payload.orgFocus),
    productFocus: toArray(payload.productFocus),
    regionalFocus: toArray(payload.regionalFocus),
    establishedYear: payload.establishedYear ?? new Date().getFullYear(),
    employees: payload.employees ?? "",
    website: payload.website ?? "",
    description: payload.description ?? "",
    services: toArray(payload.services).map(service => ({
      ...service,
      image: assetUrl(service.image)
    })),
    events: normalizeBackendEvents(payload.events, fallbackName, payload.website),
    regulations: toArray(payload.regulations)
  };
};
export const mergeOrganizationData = (base, overrides) => {
  const merged = {
    ...base,
    ...overrides,
    backendId: overrides.backendId ?? base.backendId,
    bannerImages: overrides.bannerImages ?? base.bannerImages,
    orgType: overrides.orgType ?? base.orgType,
    orgIndustryFocus: overrides.orgIndustryFocus ?? base.orgIndustryFocus,
    orgFocus: overrides.orgFocus ?? base.orgFocus,
    productFocus: overrides.productFocus ?? base.productFocus,
    regionalFocus: overrides.regionalFocus ?? base.regionalFocus,
    establishedYear: overrides.establishedYear ?? base.establishedYear,
    employees: overrides.employees ?? base.employees,
    website: overrides.website ?? base.website,
    services: overrides.services ?? base.services,
    events: overrides.events ?? base.events,
    regulations: overrides.regulations ?? base.regulations,
    infoSection: {
      contact: {
        ...base.infoSection.contact,
        ...(overrides.infoSection?.contact ?? {})
      },
      location: {
        ...base.infoSection.location,
        ...(overrides.infoSection?.location ?? {})
      },
      social: {
        ...base.infoSection.social,
        ...(overrides.infoSection?.social ?? {})
      }
    }
  };
  merged.infoSection.contact.title = merged.infoSection.contact.title ?? "Contact";
  merged.infoSection.contact.company = merged.infoSection.contact.company ?? merged.orgName;
  merged.infoSection.contact.email = merged.infoSection.contact.email ?? "";
  merged.infoSection.contact.phone = merged.infoSection.contact.phone ?? "";
  merged.infoSection.location.title = merged.infoSection.location.title ?? "Location";
  merged.infoSection.location.company = merged.infoSection.location.company ?? merged.orgName;
  merged.infoSection.location.address = merged.infoSection.location.address ?? "";
  merged.infoSection.location.city = merged.infoSection.location.city ?? "";
  merged.infoSection.location.address2 = merged.infoSection.location.address2 ?? "";
  merged.infoSection.location.state = merged.infoSection.location.state ?? "";
  merged.infoSection.location.postalCode = merged.infoSection.location.postalCode ?? "";
  merged.infoSection.location.country = merged.infoSection.location.country ?? "";
  merged.infoSection.social.title = merged.infoSection.social.title ?? "Follow Us";
  merged.infoSection.social.facebook = merged.infoSection.social.facebook ?? "";
  merged.infoSection.social.instagram = merged.infoSection.social.instagram ?? "";
  merged.infoSection.social.twitter = merged.infoSection.social.twitter ?? "";
  merged.infoSection.social.linkedIn = merged.infoSection.social.linkedIn ?? "";
  merged.infoSection.social.youtube = merged.infoSection.social.youtube ?? "";
  merged.infoSection.social.whatsApp = merged.infoSection.social.whatsApp ?? "";
  merged.infoSection.social.tikTok = merged.infoSection.social.tikTok ?? "";
  merged.infoSection.social.telegram = merged.infoSection.social.telegram ?? "";
  merged.bannerImages = merged.bannerImages.map(img => assetUrl(img));
  merged.logoUrl = assetUrl(merged.logoUrl);
  return merged;
};
const normalizeBackendOrganization = payload => {
  const profile = payload.profile ?? {};
  const staticMatch = payload.slug && findStaticOrganization(payload.slug) || payload.id && findStaticOrganization(payload.id) || undefined;
  const base = staticMatch ? convertRawOrganizationToDetail(staticMatch) : createPlaceholderOrganization(payload);
  const preferredId = staticMatch?.id ?? payload.id ?? base.id;
  const preferredSlug = staticMatch?.slug ?? payload.slug ?? base.slug ?? "";
  const resolvedOrgName = payload.orgName ?? profile.orgName ?? payload.name ?? base.orgName;
  const resolvedDescription = payload.description ?? profile.description ?? base.description;
  const resolvedWebsite = payload.website ?? profile.website ?? base.website;
  const resolvedEvents = payload.events ?? profile.events;
  const resolvedServices = payload.services ?? profile.services;
  const resolvedInfoSection = payload.infoSection ?? profile.infoSection;
  const overrides = {
    backendId: payload.id ?? base.backendId,
    id: preferredId,
    slug: preferredSlug,
    orgName: resolvedOrgName,
    orgDescripTitle: payload.orgDescripTitle ?? profile.orgDescripTitle ?? base.orgDescripTitle ?? "About Us",
    orgDescrip: payload.orgDescrip ?? profile.orgDescrip ?? payload.description ?? profile.description ?? base.orgDescrip,
    description: resolvedDescription,
    logoUrl: payload.logoUrl || profile.logoUrl ? assetUrl(payload.logoUrl ?? profile.logoUrl ?? base.logoUrl) : base.logoUrl,
    bannerImages: payload.bannerImages || profile.bannerImages ? (payload.bannerImages ?? profile.bannerImages ?? []).map(img => assetUrl(img)) : base.bannerImages,
    orgType: payload.orgType ?? profile.orgType ?? base.orgType,
    orgIndustryFocus: payload.orgIndustryFocus ?? profile.orgIndustryFocus ?? base.orgIndustryFocus,
    orgFocus: payload.orgFocus ?? profile.orgFocus ?? base.orgFocus,
    productFocus: payload.productFocus ?? profile.productFocus ?? base.productFocus,
    regionalFocus: payload.regionalFocus ?? profile.regionalFocus ?? base.regionalFocus,
    establishedYear: payload.establishedYear ?? profile.establishedYear ?? base.establishedYear,
    employees: payload.employees ?? profile.employees ?? base.employees,
    website: resolvedWebsite,
    services: resolvedServices ? resolvedServices.map(service => ({
      ...service,
      image: assetUrl(service.image)
    })) : base.services,
    events: normalizeBackendEvents(resolvedEvents, resolvedOrgName, resolvedWebsite),
    regulations: payload.regulations ?? profile.regulations ?? base.regulations,
    infoSection: resolvedInfoSection ? {
      contact: {
        ...base.infoSection.contact,
        ...(profile.infoSection?.contact ?? {}),
        ...(payload.infoSection?.contact ?? {})
      },
      location: {
        ...base.infoSection.location,
        ...(profile.infoSection?.location ?? {}),
        ...(payload.infoSection?.location ?? {})
      },
      social: {
        ...base.infoSection.social,
        ...(profile.infoSection?.social ?? {}),
        ...(payload.infoSection?.social ?? {})
      }
    } : base.infoSection
  };
  return mergeOrganizationData(base, overrides);
};
const getBackendFetchState = () => {
  const globalScope = globalThis;
  if (!globalScope.__prodCertOrgFetchState) {
    globalScope.__prodCertOrgFetchState = {};
  }
  return globalScope.__prodCertOrgFetchState;
};
const shouldThrottleBackendFetch = () => {
  const state = getBackendFetchState();
  if (state.cooldownUntil && Date.now() < state.cooldownUntil) {
    if (process.env.NODE_ENV !== "production" && !state.active) {
      console.warn("Skipping organizations backend fetch during cooldown window");
    }
    return true;
  }
  if (state.active) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Detected recursive organizations backend fetch. Short-circuiting to prevent infinite loop.");
    }
    return true;
  }
  return false;
};
const markBackendFetchStart = () => {
  const state = getBackendFetchState();
  state.active = true;
};
const markBackendFetchEnd = () => {
  const state = getBackendFetchState();
  state.active = false;
};
const markBackendFetchCooldown = durationMs => {
  const state = getBackendFetchState();
  state.cooldownUntil = Date.now() + durationMs;
};

// Normalize identifiers so we can dedupe static + backend entries even when
// slugs/ids differ by punctuation, spacing, or casing.
const canonicalizeIdentifier = value => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const getCanonicalOrgKey = org => {
  const candidates = [org.backendId, org.id, org.slug, org.orgName ?? org.name];

  // Try a slugified version of the name too in case slug is absent/mismatched
  const name = org.orgName ?? org.name;
  if (name) {
    candidates.push(slugify(name));
  }
  for (const candidate of candidates) {
    const key = canonicalizeIdentifier(candidate);
    if (key) return key;
  }
  return "";
};
const fetchBackendOrganizations = async () => {
  if (!API_BASE_URL) return [];
  if (shouldThrottleBackendFetch()) return [];
  markBackendFetchStart();
  try {
    const response = await fetch(`${API_BASE_URL}/organizations`, {
      credentials: "include"
    });
    if (!response.ok) {
      if (response.status >= 500) {
        markBackendFetchCooldown(60_000);
      }
      return [];
    }
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.toLowerCase().includes("application/json")) {
      if (process.env.NODE_ENV !== "production") {
        try {
          const preview = (await response.text()).slice(0, 200);
          console.warn("Organizations API returned non-JSON response:", preview.replace(/\s+/g, " ").trim());
        } catch {
          console.warn("Organizations API returned non-JSON response and preview failed to read");
        }
      }
      markBackendFetchCooldown(60_000);
      return [];
    }
    const json = await response.json();
    return Array.isArray(json) ? json : [];
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Failed to fetch organizations from backend", error);
    }
    markBackendFetchCooldown(60_000);
    return [];
  } finally {
    markBackendFetchEnd();
  }
};
const fetchBackendOrganizationById = async id => {
  if (!API_BASE_URL || !id) return null;
  if (shouldThrottleBackendFetch()) return null;
  markBackendFetchStart();
  try {
    const response = await fetch(`${API_BASE_URL}/organizations/${encodeURIComponent(id)}`, {
      credentials: "include"
    });
    if (!response.ok) {
      if (response.status >= 500) {
        markBackendFetchCooldown(60_000);
      }
      return null;
    }
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.toLowerCase().includes("application/json")) {
      if (process.env.NODE_ENV !== "production") {
        try {
          const preview = (await response.text()).slice(0, 200);
          console.warn(`Organizations API returned non-JSON response for id ${id}:`, preview.replace(/\s+/g, " ").trim());
        } catch {
          console.warn(`Organizations API returned non-JSON response for id ${id} and preview failed to read`);
        }
      }
      markBackendFetchCooldown(60_000);
      return null;
    }
    const json = await response.json();
    return json ?? null;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`Failed to fetch organization ${id} from backend`, error);
    }
    markBackendFetchCooldown(60_000);
    return null;
  } finally {
    markBackendFetchEnd();
  }
};

// ------------------------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------------------------

export const fetchOrganizations = async () => {
  const detailMap = new Map();
  const upsert = (detail, source) => {
    const key = getCanonicalOrgKey(detail);
    if (!key) return;
    const existing = detailMap.get(key);
    // Prefer backend data over static when keys collide
    if (!existing || source === "backend") {
      detailMap.set(key, {
        detail,
        source
      });
    }
  };

  // Static data seeding removed — only backend data is used now

  const backend = await fetchBackendOrganizations();
  backend.forEach(payload => {
    const detail = normalizeBackendOrganization(payload);
    upsert(detail, "backend");
  });

  // Fallback: seed static data when backend is unavailable so the page
  // still renders on hard refresh instead of showing empty state.
  if (detailMap.size === 0) {
    Object.values(organizationsData).forEach(raw => {
      const detail = convertRawOrganizationToDetail(raw);
      upsert(detail, "static");
    });
  }
  return Array.from(detailMap.values()).map(({
    detail
  }) => convertDetailToListItem(detail)).sort((a, b) => a.orgName.localeCompare(b.orgName));
};
export const fetchOrganizationBySlug = async identifier => {
  const normalized = String(identifier || "").trim();
  if (!normalized) return null;

  // Try direct backend fetch by identifier
  const candidateIds = new Set();
  candidateIds.add(normalized);
  for (const id of Array.from(candidateIds.values())) {
    if (!id) continue;
    const payload = await fetchBackendOrganizationById(id);
    if (payload) {
      if (!payload.slug) {
        payload.slug = slugify(payload.name ?? id);
      }
      return normalizeBackendOrganization(payload);
    }
  }

  // Fallback: fetch collection and try to match by slug/id/name in case
  // the singular endpoint is unavailable for this identifier.
  const targetKey = canonicalizeIdentifier(normalized);
  const backendAll = await fetchBackendOrganizations();
  const found = backendAll.find(item => {
    const candidates = [item.slug, item.id, item.name, slugify(item.name ?? "")];
    return candidates.some(c => canonicalizeIdentifier(c) === targetKey);
  });
  if (found) {
    if (!found.slug) {
      found.slug = slugify(found.name ?? normalized);
    }
    return normalizeBackendOrganization(found);
  }

  // Fallback: look up static data when backend is unavailable
  const staticMatch = findStaticOrganization(normalized);
  if (staticMatch) {
    return convertRawOrganizationToDetail(staticMatch);
  }
  return null;
};

// ------------------------------------------------------------------------------------
// Static dataset (legacy fallback + seed content)
// ------------------------------------------------------------------------------------

export const organizationsData = {
  productCertification: {
    id: "productCertification",
    slug: "ProductCertification",
    orgName: "ProductCertification.com",
    orgDescripTitle: "Helping manufacturers design, certify, and ship products that are safe, compliant, and green — Ship It!",
    orgDescrip: "At Product Certification, we're dedicated to helping manufacturers navigate the complex world of product compliance. In today's rapidly evolving global marketplace, bringing a new product to market involves much more than just design and production. It requires a deep understanding of intricate regulations related to product safety, RF, EMC, energy efficiency, and environmental compliance.\n\nOur mission is simple: to empower you to design, certify, and ship products that are not only innovative and high-quality but also safe, compliant, and green. We act as your trusted technical consulting partner, providing expert guidance throughout every stage of the product lifecycle. From initial design considerations to final certification and market access, we're here to streamline the process, minimize risks, and ensure your products meet all necessary national and international standards.\n\nLet us help you achieve compliance with confidence, allowing you to focus on what you do best: creating exceptional products.",
    logoUrl: "/PlaceholderImgs/checkLogo.png",
    bannerImages: ["/PlaceholderImgs/Buildings.jpg", "/PlaceholderImgs/handshake.jpg", "/PlaceholderImgs/laptop.webp", "/PlaceholderImgs/meeting.webp", "/PlaceholderImgs/reviews.jpg"],
    infoSection: {
      contact: {
        title: "Contact Us",
        company: "ProductCertification.com",
        email: "cassano@productcertification.com",
        phone: "+1 (800) 555-0123"
      },
      location: {
        title: "Location",
        company: "Product Certification",
        address: "1 World Way, Suite D",
        city: "Los Angeles, CA 91367 USA"
      },
      social: {
        title: "Follow Us",
        facebook: "https://google.com",
        instagram: "https://google.com",
        twitter: "https://google.com",
        linkedIn: "https://google.com"
      }
    },
    orgType: ["Consulting", "Certification Tools & Services"],
    orgIndustryFocus: ["Consumer Electronics", "Information Technology", "Fire & Life Safety"],
    orgFocus: ["Product Safety", "RF/EMC", "Environmental", "Energy Efficiency", "Restricted Substances", "Global Market Access"],
    productFocus: ["Smartphones", "Tablets", "Computers", "Wearables", "Smarthome", "Audio Visual", "External Power Supplies"],
    regionalFocus: ["US", "Canada", "Europe", "Global"],
    establishedYear: 2018,
    employees: "10-50",
    website: "https://productcertification.com",
    description: "Leading product compliance consulting firm specializing in helping manufacturers navigate complex regulatory requirements.",
    services: [{
      id: 1,
      title: "Product Safety Testing",
      description: "Comprehensive safety testing for consumer electronics and electrical products",
      category: "Testing",
      price: 2500,
      duration: "2-4 weeks",
      image: "/img/faces/christian.jpg"
    }, {
      id: 2,
      title: "EMC/RF Compliance",
      description: "Electromagnetic compatibility and radio frequency testing services",
      category: "Testing",
      price: 3500,
      duration: "3-5 weeks",
      image: "/img/faces/kendall.jpg"
    }, {
      id: 3,
      title: "Environmental Testing",
      description: "RoHS, REACH, and environmental compliance testing",
      category: "Testing",
      price: 1800,
      duration: "1-2 weeks",
      image: "/img/faces/avatar.jpg"
    }, {
      id: 4,
      title: "Regulatory Consulting",
      description: "Expert guidance on global regulatory requirements and market access",
      category: "Consulting",
      price: 250,
      duration: "Hourly",
      image: "/img/faces/marc.jpg"
    }, {
      id: 5,
      title: "Global Market Access",
      description: "End-to-end support for international product launches and certifications",
      category: "Consulting",
      price: 5000,
      duration: "4-8 weeks",
      image: "/img/faces/christian.jpg"
    }, {
      id: 6,
      title: "Energy Efficiency Certification",
      description: "ENERGY STAR and other energy efficiency certifications",
      category: "Certification",
      price: 2800,
      duration: "3-4 weeks",
      image: "/img/faces/kendall.jpg"
    }],
    events: [{
      id: "digital-marketing-workshop",
      slug: "digital-marketing-workshop",
      image: "/img/faces/christian.jpg",
      header: "Workshop: Digital Marketing",
      subheader: "Learn effective strategies",
      description: "A comprehensive workshop covering modern digital marketing techniques and best practices.",
      ctaText: "Register Now",
      price: 99,
      currency: "USD",
      date: "2024-02-15",
      purchaseURL: "#",
      organizer: "ProductCertification.com",
      category: "Workshop",
      tags: ["digital marketing", "workshop", "training"],
      isVirtual: false
    }, {
      id: "annual-conference",
      slug: "annual-conference",
      image: "/img/faces/kendall.jpg",
      header: "Annual Conference",
      subheader: "Industry Leaders Summit",
      description: "Join industry experts for insights into the future of business and technology.",
      ctaText: "Book Ticket",
      price: 299,
      currency: "USD",
      date: "2024-03-20",
      purchaseURL: "#",
      organizer: "ProductCertification.com",
      category: "Conference",
      tags: ["conference", "industry leaders", "networking"],
      isVirtual: false
    }, {
      id: "networking-event",
      slug: "networking-event",
      image: "/img/faces/avatar.jpg",
      header: "Networking Event",
      subheader: "Connect & Collaborate",
      description: "Build meaningful professional relationships in a relaxed environment.",
      ctaText: "RSVP",
      price: 25,
      currency: "USD",
      date: "2024-02-28",
      purchaseURL: "#",
      organizer: "ProductCertification.com",
      category: "Networking",
      tags: ["networking", "collaboration", "professional"],
      isVirtual: false
    }, {
      id: "skills-bootcamp",
      slug: "skills-bootcamp",
      image: "/img/faces/marc.jpg",
      header: "Skills Bootcamp",
      subheader: "Intensive Training",
      description: "Fast-track your career with our intensive skills development program.",
      ctaText: "Enroll",
      price: 199,
      currency: "USD",
      date: "2024-04-10",
      purchaseURL: "#",
      organizer: "ProductCertification.com",
      category: "Bootcamp",
      tags: ["bootcamp", "skills", "training", "career"],
      isVirtual: false
    }],
    regulations: ["eu-ecodesign-external-power-supplies", "fcc-part-15-subpart-b-unintentional-radiators", "ce-marking-emc-directive"]
  }
};

// ------------------------------------------------------------------------------------
// Additional helpers used elsewhere in the app
// ------------------------------------------------------------------------------------

export const fetchRegulationsByIds = async ids => {
  await new Promise(resolve => setTimeout(resolve, 100));
  try {
    return ids.map(id => regulationsData[id]).filter(regulation => regulation !== undefined);
  } catch (error) {
    console.error("Error fetching regulations by IDs:", error);
    return [];
  }
};
export const getAllOrganizationSlugs = async () => {
  const slugs = new Set();
  const backend = await fetchBackendOrganizations();
  backend.forEach(payload => {
    const candidate = payload.slug ?? payload.id ?? (payload.name ? slugify(payload.name) : undefined);
    if (candidate) {
      slugs.add(candidate);
    }
  });
  return Array.from(slugs.values()).sort();
};